/**
 * Turns a failed payment write into copy an operator can act on, without
 * echoing the backend's own text.
 *
 * The overpay guards raise the one message worth showing on this surface —
 * it names what the document still owes against what was attempted, which is
 * exactly the number the operator needs to correct the amount. But they raise
 * it as
 * `Overpayment blocked for gl_customer_payments. Use
 * xan_apply_customer_payment(). Outstanding 250.00, attempted 400.00`,
 * which also names a table and an internal function. Rendering that verbatim
 * leaks schema detail (CWE-209) and spends half the alert on a function name
 * that means nothing to the person reading it.
 *
 * So the guard is recognised and re-stated: the money survives, the schema
 * does not, and every unrecognised failure collapses to fixed copy with the
 * original error going to the structured log via `reportSuppressedError`.
 */

import { reportSuppressedError } from "@/lib/errors";
import { formatCurrency } from "@/lib/format";

/** Shown for any failure that is not a recognised business denial. */
export const PAYMENT_WRITE_GENERIC_MESSAGE =
  "Something went wrong saving this payment. Try again, and if it keeps failing, take a note of the payment UID before asking for help.";

export const PAYMENT_DELETE_GENERIC_MESSAGE =
  "Something went wrong deleting this payment. Try again, and if it keeps failing, take a note of the payment UID before asking for help.";

/**
 * `Outstanding %, attempted %` as the guards format it. The table name and the
 * `xan_apply_*` hint are matched so the guard is identified precisely, and
 * discarded so neither reaches the screen.
 */
const OVERPAY_GUARD =
  /Overpayment blocked for gl_(?:customer|vendor)_payments\b[\s\S]*?Outstanding\s+(-?[\d.]+),\s*attempted\s+(-?[\d.]+)/;

/** The document a payment settles, named the way the operator knows it. */
export type PaymentDocumentLabel = "invoice" | "purchase order";

function overpayMessage(raw: string, document: PaymentDocumentLabel): string | null {
  const match = OVERPAY_GUARD.exec(raw);
  if (!match) {
    return null;
  }

  const outstanding = Number(match[1]);
  const attempted = Number(match[2]);
  if (!(Number.isFinite(outstanding) && Number.isFinite(attempted))) {
    return null;
  }

  return `That amount is more than the ${document} still owes. Outstanding ${formatCurrency(outstanding)}, attempted ${formatCurrency(attempted)}. Lower the amount, or record the difference as a credit from the ${document}.`;
}

/**
 * Business denials worth restating on a delete.
 *
 * Each pattern is matched against a real `RAISE EXCEPTION` in the payment
 * delete RPCs. The copy is a rewrite, not a passthrough: the raw text
 * interpolates payment, credit, and application UUIDs and names the staff
 * profile check, none of which helps the person reading the toast and all of
 * which is implementation detail once the actionable part is stated plainly.
 */
const DELETE_DENIALS: { message: string; pattern: RegExp }[] = [
  {
    // 'Vendor payment % has a remainder vendor credit (%) with applied
    // application(s) %; reverse those applications before deleting the payment.'
    pattern: /remainder vendor credit[\s\S]*applied application/i,
    message:
      "This payment's overpayment credit has already been applied elsewhere. Reverse those credit applications first, then delete the payment.",
  },
  {
    // 'Payment delete requires an approved active staff profile.'
    pattern: /requires an approved active staff profile/i,
    message: "Your account is not approved to delete payments. Ask an administrator for access.",
  },
  {
    // 'Vendor payment % changed purchase order during delete; retry.'
    pattern: /changed purchase order during delete/i,
    message: "This payment changed while the delete was running. Reload the list and try again.",
  },
];

function rawMessage(error: unknown): string {
  return error instanceof Error ? error.message : "";
}

/**
 * Operator-facing copy for a rejected payment update.
 *
 * The raw error always reaches the log; only the returned string reaches the
 * screen. `context` is the log label, so it should name the write.
 */
export function paymentWriteErrorMessage(
  context: string,
  error: unknown,
  document: PaymentDocumentLabel,
): string {
  reportSuppressedError(context, error);
  return overpayMessage(rawMessage(error), document) ?? PAYMENT_WRITE_GENERIC_MESSAGE;
}

/**
 * Operator-facing copy for a rejected payment delete.
 *
 * A delete cannot trip the overpay guard, but it has denials of its own that
 * the operator can act on — an applied overpayment credit, a missing staff
 * approval, a concurrent re-parent. Those are restated; anything else collapses
 * to fixed copy.
 */
export function paymentDeleteErrorMessage(context: string, error: unknown): string {
  reportSuppressedError(context, error);

  const raw = rawMessage(error);
  return (
    DELETE_DENIALS.find((denial) => denial.pattern.test(raw))?.message ??
    PAYMENT_DELETE_GENERIC_MESSAGE
  );
}

/**
 * Operator-facing copy for a finance-document write failure with no business
 * denial worth restating — deleting a purchase order, reversing a credit
 * application, applying a credit. Unlike the payment helpers above, none of
 * these RPCs raise a guard this module knows how to identify and rewrite, so
 * every failure collapses to the caller's own fixed copy; only the raw error
 * reaches the log, through the same `reportSuppressedError` path.
 */
export function financeWriteErrorMessage(
  context: string,
  error: unknown,
  genericMessage: string,
): string {
  reportSuppressedError(context, error);
  return genericMessage;
}

/**
 * Operator-facing detail line for a failed *read*.
 *
 * Deliberately pure. The write helpers above log through
 * `reportSuppressedError` because they run inside a mutation callback, but a
 * read failure is consumed during render, where logging would re-fire on every
 * re-render and turn one failed query into an unbounded log. A read carries no
 * business denial worth allowlisting either, so there is nothing to classify:
 * the banner keeps its own headline and this supplies the line beneath it.
 */
export function paymentReadErrorDetails(error: Error | null | undefined): string | undefined {
  return error
    ? "The record could not be read. Retry, or reload if this keeps happening."
    : undefined;
}
